import { toCamelCase } from "@akina/utils";
import type {
  RegistrationStatusResponse,
  RegistrationStatusResponseApi,
  WebshopCheckoutUrlResponseApi,
  WebshopInvitePatientRequestApi,
  WebshopInvitePatientResponse,
  WebshopInvitePatientResponseApi,
  WebshopTokenResponse,
  WebshopTokenResponseApi,
} from "types/schemas";
import { ApiMethod, type ApiConfiguration } from ".";

type WebshopCheckoutUrl = {
  checkoutUrl: string | null;
} | null;

export const webshopApi = (config: ApiConfiguration) => {
  const fetchCamelCaseNullable = async <TResponseApi, TResponse>(
    params: Parameters<typeof config.httpService.fetch<TResponseApi>>[0],
  ): Promise<TResponse | null> => {
    const result = (await config.httpService.fetch<TResponseApi>(params)) as TResponseApi;
    if (result === null) return null;
    return toCamelCase<TResponseApi, TResponse>(result);
  };

  const fetchCamelCaseNonNull = async <TResponseApi, TResponse>(
    params: Parameters<typeof config.httpService.fetch<TResponseApi>>[0],
  ): Promise<TResponse> => {
    const result = await fetchCamelCaseNullable<TResponseApi, TResponse>(params);
    if (result === null) throw new Error("Expected webshop response");
    return result;
  };

  return {
    async cancel(): Promise<void> {
      await config.httpService.fetch({
        method: ApiMethod.POST,
        url: config.endpoints.webshop.cancelSubscription,
      });
    },

    renew(params: { isApp: boolean }): Promise<WebshopCheckoutUrl> {
      return fetchCamelCaseNullable<WebshopCheckoutUrlResponseApi | null, NonNullable<WebshopCheckoutUrl>>({
        method: ApiMethod.POST,
        url: config.endpoints.webshop.renewSubscription,
        data: { is_app: params.isApp },
      });
    },

    updatePaymentMethod(params: { isApp: boolean }): Promise<WebshopCheckoutUrl> {
      return fetchCamelCaseNullable<WebshopCheckoutUrlResponseApi | null, NonNullable<WebshopCheckoutUrl>>({
        method: ApiMethod.PATCH,
        url: config.endpoints.webshop.updatePaymentMethod,
        data: { is_app: params.isApp },
      });
    },

    getToken(): Promise<WebshopTokenResponse> {
      return fetchCamelCaseNonNull<WebshopTokenResponseApi, WebshopTokenResponse>({
        method: ApiMethod.GET,
        url: config.endpoints.webshop.token,
      });
    },

    invitePatient(data: WebshopInvitePatientRequestApi): Promise<WebshopInvitePatientResponse> {
      return fetchCamelCaseNonNull<WebshopInvitePatientResponseApi, WebshopInvitePatientResponse>({
        method: ApiMethod.POST,
        url: config.endpoints.webshop.invitePatient,
        data,
      });
    },

    getRegistrationStatus(token: string): Promise<RegistrationStatusResponse> {
      return fetchCamelCaseNonNull<RegistrationStatusResponseApi, RegistrationStatusResponse>({
        method: ApiMethod.GET,
        url: config.endpoints.webshop.registrationStatus(token),
      });
    },
  };
};
